import { Component, OnDestroy } from '@angular/core';
import { TAccountSimpleResponse } from '@project/view-models';
import { Subject, takeUntil } from 'rxjs';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { NEW_FOLDER_ID } from '../../../account-list/utils/account-list-constants';
import {
  ADD_COLLAPSED_ACCOUNT,
  ADD_COLLAPSED_FOLDER,
  CLEAR_COLLAPSED_ACCOUNTS,
  CLEAR_COLLAPSED_FOLDERS,
  GET_COLLAPSED_ACCOUNTS,
  GET_COLLAPSED_FOLDERS,
  GET_FAVOURITES_COLLAPSED,
  getExpandedFoldersChange$,
  SEND_ACCOUNT_LIST_UPDATE_EVENT,
  SET_FAVOURITES_COLLAPSED,
} from '../../../account-list/utils/account-list.ui.store';
import { SidePanelExpandAllService } from './side-panel-expand-all.service';
import { SidePanelSitesListService } from '../side-panel-sites-list.service';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-side-panel-expand-all-button',
  templateUrl: './side-panel-expand-all-button.component.html',
  styleUrl: './side-panel-expand-all-button.component.scss',
  standalone: false,
})
export class SidePanelExpandAllButtonComponent implements OnDestroy {
  constructor(
    private sidePanelExpandAllService: SidePanelExpandAllService,
    private sidePanelSitesListService: SidePanelSitesListService,
  ) {}

  private accounts: TAccountSimpleResponse[] = [];
  private readonly destroy$ = new Subject<void>();
  allCollapsed: boolean;
  EIconPath = EIconPath;
  iconMap = iconMap;

  private idsToCheck: {
    [accountId: string]: string[];
  } = {};

  ngOnInit(): void {
    this.sidePanelSitesListService
      .fetchWorkspaces()
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.updateIdsToCheck(accounts);
      });

    this.sidePanelSitesListService.accountsChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.updateIdsToCheck(accounts);
      });

    this.allCollapsed = this.checkIfAllCollapsed();

    getExpandedFoldersChange$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.allCollapsed = this.checkIfAllCollapsed();
      });
  }

  private updateIdsToCheck(accounts: TAccountSimpleResponse[]): void {
    this.accounts = accounts;
    this.idsToCheck = {};

    this.accounts.forEach((account) => {
      this.idsToCheck[account.id] = [];
      const folders = account.accountFolders || [];

      folders.forEach((folder) => {
        if (folder.id !== NEW_FOLDER_ID) {
          this.idsToCheck[account.id].push(folder.id);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  toggleExpandAll(): void {
    if (this.allCollapsed) {
      this.expandAll();
    } else {
      this.collapseAll();
    }
  }

  private expandAll(): void {
    SET_FAVOURITES_COLLAPSED(false, false);
    CLEAR_COLLAPSED_ACCOUNTS(false);
    CLEAR_COLLAPSED_FOLDERS(false);
    SEND_ACCOUNT_LIST_UPDATE_EVENT(true);
    this.sidePanelExpandAllService.triggerCollapseChange();
  }

  private collapseAll(): void {
    SET_FAVOURITES_COLLAPSED(true, false);

    Object.keys(this.idsToCheck).forEach((accountId) => {
      ADD_COLLAPSED_ACCOUNT(accountId, false);

      this.idsToCheck[accountId].forEach((folderId) => {
        ADD_COLLAPSED_FOLDER(accountId, folderId, false);
      });
    });

    SEND_ACCOUNT_LIST_UPDATE_EVENT(true);
    this.sidePanelExpandAllService.triggerCollapseChange();
  }

  private checkIfAllCollapsed(): boolean {
    const favouritesCollapsed = GET_FAVOURITES_COLLAPSED();
    const collapsedAccounts = GET_COLLAPSED_ACCOUNTS();
    const collapsedFolders = GET_COLLAPSED_FOLDERS();

    if (!favouritesCollapsed) {
      return false;
    }

    for (const accountId of Object.keys(this.idsToCheck)) {
      if (!collapsedAccounts.has(accountId)) {
        return false;
      }

      const folders = this.idsToCheck[accountId];
      for (const folderId of folders) {
        const mainAccountCollapsed = collapsedAccounts.has(accountId);

        if (!mainAccountCollapsed && !collapsedFolders[accountId].includes(folderId)) {
          return false;
        }
      }
    }

    return true;
  }
}
